// Components
import { TooltipDecorator } from '@molecules/index';
import { VesselRatingSummary } from '@molecules/index';
// Types
import { DateRangeType, UtilityComponents } from '@globalTypes/index';
import { BarTooltipWrapperProps } from './BarTooltipWrapper.types';
// Utils
import { pluralizeString } from '@helpers/index';
import text from '@translations/en.json';

/**
 * BarTooltipWrapper is a component that wraps a Victory bar tooltip with custom content.
 * It displays a tooltip with rating information and quantity details for a bar chart data
 * point.
 *
 * @component
 * @param {BarTooltipWrapperProps} props - Props for the BarTooltipWrapper component.
 * @param {VesselRatingSummary} props.datum - The data point object for which the tooltip is being rendered.
 * @param {number} [props.x=0] - The x-coordinate of the tooltip position.
 * @param {number} [props.y=0] - The y-coordinate of the tooltip position.
 * @returns {ReactElement} A component that renders a custom tooltip for a Victory bar.
 */
export const BarTooltipWrapper = ({ datum, x = 0, y = 0 }: BarTooltipWrapperProps) => {
  // Destructure properties from the datum object, the assertion comes from the
  // the non type model of victory to use {datum}.
  const { quantity, typeData, rating } = datum as VesselRatingSummary;
  const xAxisCorrection = x - 45;
  const title = typeData === DateRangeType.endOfYear ? text.forecasted_cii : text.current_cii;
  const vesselLabel = pluralizeString(text.vessel_label, quantity);
  const description = `${quantity} ${vesselLabel}`;

  return (
    <TooltipDecorator
      type={UtilityComponents.RatingInfoCompacted}
      title={title}
      positionObject={{
        x: xAxisCorrection,
        y,
      }}
      params={{
        rating,
        description,
      }}
    />
  );
};
